import { useProjectOverviewQuery } from "@/api/hooks";
import { usePreviewCandidatesQuery } from "@/api/preview";
import { EM_DASH } from "@/lib/format";
import { candidateForRun, firstEventTime } from "@/lib/labExport";
import type { LabRunModel } from "@/lib/labRunModel";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";

/**
 * Whole run → Overview → "Identifiers": every id a person needs to find this
 * run elsewhere. The Brain conversation id is NOT listed: the Editor receives
 * none (the run envelope, dispatch and trace identity carry no conversation
 * id, by design), so there is nothing honest to show.
 */
export function LabIdentifiers({
  projectId,
  model,
}: {
  projectId: string;
  model: LabRunModel;
}) {
  const overview = useProjectOverviewQuery(projectId);
  const candidates = usePreviewCandidatesQuery(projectId);
  const candidate = candidateForRun(model, candidates.data?.items ?? []);
  const identity = (axis: string) =>
    model.overview.identity.find((row) => row.axis === axis)?.value ?? null;
  const started = firstEventTime(model);
  const sim = model.sim;

  const rows: [string, string | null][] = [
    ["Preview run ref", model.previewRunRef || null],
    ["Candidate id", candidate?.candidateId ?? null],
    ["Candidate hash", identity("candidateContentHash")],
    ["Base pack commit", identity("basePackSha")],
    ["Tenant", overview.data?.project.previewTenantId ?? null],
    ["Brain commit", identity("servingBrainCommit")],
    ["LLM model", identity("model")],
    ["Persona", sim?.personaId ?? null],
    ["Language", sim?.language ?? null],
    ["Created", started],
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Identifiers</CardTitle>
      </CardHeader>
      <CardBody>
        <dl className="space-y-2" aria-label="Run identifiers">
          {rows.map(([label, value]) => (
            <div key={label}>
              <dt className="text-xs font-medium text-muted-foreground">
                {label}
              </dt>
              <dd
                dir="ltr"
                className="select-all break-all font-mono text-xs text-foreground"
              >
                {value ?? EM_DASH}
              </dd>
            </div>
          ))}
        </dl>
      </CardBody>
    </Card>
  );
}
